import { useQuery } from '@tanstack/react-query';

import { searchPartnerTransactions } from '~/services/LuxLoyaltyService';

import type {
  AdminPartnerTransaction,
  AdminPartnerTransactionCounts,
  AdminPartnerTransactionSearchBody,
} from '~/types/loyaltyContract';

export const partnerTransactionQueryKeys = {
  all: ['partnerTransactions'] as const,
  searches: () => [...partnerTransactionQueryKeys.all, 'search'] as const,
  search: (partnerId: string, filters: AdminPartnerTransactionSearchBody) =>
    [...partnerTransactionQueryKeys.searches(), partnerId, filters] as const,
};

/** The endpoint caps each id filter at 100, so a bulk run is asked about in chunks of this size. */
export const PARTNER_TRANSACTION_FILTER_LIMIT = 100;

/** How often to re-ask while rows are still queued. */
const OUTCOME_POLL_INTERVAL_MS = 5000;

/**
 * Stops polling a run that never settles — a row wedged in PENDING (a stalled consumer, say)
 * should not have the screen asking forever. Roughly two minutes at the interval above.
 */
const MAX_OUTCOME_POLLS = 24;

interface UseSearchPartnerTransactionsOptions {
  enabled?: boolean;
}

/**
 * Searches one partner's transactions.
 *
 * Credits are queued and processed asynchronously, so a row's real outcome only exists here —
 * the submission response says "accepted", never "credited".
 */
export function useSearchPartnerTransactions(
  partnerId: string | undefined,
  filters: AdminPartnerTransactionSearchBody,
  options: UseSearchPartnerTransactionsOptions = {},
) {
  return useQuery({
    queryKey: partnerTransactionQueryKeys.search(partnerId ?? '', filters),
    queryFn: async ({ signal }) => {
      return await searchPartnerTransactions(partnerId as string, filters, { signal });
    },
    enabled: !!partnerId && (options.enabled ?? true),
  });
}

function chunk<T>(items: Array<T>, size: number): Array<Array<T>> {
  const chunks: Array<Array<T>> = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export interface PartnerEarnOutcomes {
  counts: AdminPartnerTransactionCounts;
  failed: Array<AdminPartnerTransaction>;
}

/**
 * Asks how a bulk run actually turned out, by the idempotency keys it submitted.
 *
 * The submission response only ever says "accepted" — a row can still fail downstream at IBS, and
 * the wizard would otherwise keep reporting it as a success. Keys are sent in chunks because the
 * endpoint caps each id filter at 100, and the per-chunk counts sum because the key sets are
 * disjoint. Only failed rows are collected in full; they are the ones an admin has to act on.
 */
export function usePartnerEarnOutcomes(partnerId: string | undefined, idempotencyKeys: Array<string>) {
  return useQuery({
    queryKey: [...partnerTransactionQueryKeys.searches(), 'outcomes', partnerId ?? '', idempotencyKeys],
    queryFn: async ({ signal }): Promise<PartnerEarnOutcomes> => {
      const counts: AdminPartnerTransactionCounts = { total: 0, pending: 0, completed: 0, failed: 0 };
      const failed: Array<AdminPartnerTransaction> = [];

      for (const keys of chunk(idempotencyKeys, PARTNER_TRANSACTION_FILTER_LIMIT)) {
        const response = await searchPartnerTransactions(
          partnerId as string,
          { idempotencyKeys: keys, statuses: ['FAILED'], pageSize: PARTNER_TRANSACTION_FILTER_LIMIT },
          { signal },
        );
        counts.total += response.result.counts.total;
        counts.pending += response.result.counts.pending;
        counts.completed += response.result.counts.completed;
        counts.failed += response.result.counts.failed;
        failed.push(...response.result.transactions);
      }

      return { counts, failed };
    },
    enabled: !!partnerId && idempotencyKeys.length > 0,
    // The first read lands milliseconds after the last 202, before the consumer has taken anything
    // off the queue, so it would otherwise always report every row as still processing. Poll until
    // nothing is pending rather than making the operator click Refresh to see the real outcome.
    refetchInterval: (query) => {
      if (query.state.data && query.state.data.counts.pending === 0) {
        return false;
      }
      if (query.state.dataUpdateCount >= MAX_OUTCOME_POLLS) {
        return false;
      }
      return OUTCOME_POLL_INTERVAL_MS;
    },
  });
}
